import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatUserFacingError } from '@/lib/errors'
import { programApi } from './api'
import type { ProgramDraft } from './api'
export function ProductionPrograms({ productionId }: { productionId: string }) {
  const [programs, setPrograms] = useState<ProgramDraft[]>([])
  const [events, setEvents] = useState<Array<{ id: string; title: string; slug: string }>>([])
  const [eventId, setEventId] = useState('')
  const [slug, setSlug] = useState('')
  const [compatibility, setCompatibility] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(true)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    let active = true
    setBusy(true)
    void Promise.all([programApi.forProduction(productionId), programApi.events(productionId)])
      .then(([programs, events]) => {
        if (active) {
          setPrograms(programs)
          setEvents(events)
        }
      })
      .catch((e) => {
        if (active) setError(formatUserFacingError(e, 'Could not load production programs.'))
      })
      .finally(() => {
        if (active) setBusy(false)
      })
    return () => {
      active = false
    }
  }, [productionId, tick])
  async function create() {
    setBusy(true)
    setError('')
    try {
      await programApi.create({ productionId, eventId, slug, compatibility })
      setTick((t) => t + 1)
      setEventId('')
      setSlug('')
    } catch (e) {
      setError(formatUserFacingError(e, 'Could not create program.'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Digital programs</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Each performance has its own lineup, date, public URL, and audience award. Source updates
          affect drafts; publishing is a separate admin action.
        </p>
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
        {programs.map((p) => (
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded border p-4"
            key={p.id}
          >
            <div>
              <p className="font-medium">{p.showSource.title}</p>
              <p className="text-sm">
                /program/{p.slug}/ · {p.publishedDigest ? 'Published snapshot available' : 'Draft'}
              </p>
            </div>
            <Button asChild variant="outline">
              <Link to={`/admin/productions/${productionId}/program/${p.id}`}>Edit program</Link>
            </Button>
          </div>
        ))}
        {events.length === 0 ? (
          <p>
            Create a linked event for the performance to begin. Ticket sales can remain disabled.
          </p>
        ) : (
          <form
            className="space-y-3 rounded border p-4"
            onSubmit={(e) => {
              e.preventDefault()
              void create()
            }}
          >
            <Label htmlFor="program-event">Performance</Label>
            <select
              id="program-event"
              className="min-h-11 w-full rounded border bg-background px-3"
              value={eventId}
              onChange={(e) => {
                setEventId(e.target.value)
                setSlug(events.find((v) => v.id === e.target.value)?.slug ?? '')
              }}
              required
            >
              <option value="">Choose a performance</option>
              {events
                .filter((e) => !programs.some((p) => p.eventId === e.id))
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title}
                  </option>
                ))}
            </select>
            <Label htmlFor="program-slug">Stable public URL slug</Label>
            <Input
              id="program-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              required
            />
            <label className="flex min-h-11 items-center gap-2">
              <input
                type="checkbox"
                checked={compatibility}
                onChange={(e) => {
                  setCompatibility(e.target.checked)
                  if (e.target.checked) setSlug('oddfest-2026')
                }}
              />
              Prepare the approved ODDFEST 2026 compatibility draft
            </label>
            {compatibility && (
              <p className="text-sm">
                Preserves the approved twelve pieces and legacy voting IDs. Preparing this draft
                does not publish or alter ballots.
              </p>
            )}
            <Button type="submit" disabled={busy || !eventId}>
              Create draft
            </Button>
          </form>
        )}
        <Button variant="ghost" disabled={busy} onClick={() => setTick((t) => t + 1)}>
          Refresh programs
        </Button>
      </CardContent>
    </Card>
  )
}
